/**
 * This file started off from the @react-aria's `useSingleSelectListState` implementation
 * which can be found here
 * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-stately/list/src/useSingleSelectListState.ts*
 *
 * At the time of writing, @react-aria did not directly support select or
 * combobox with multiple selection. The reason being that there is no such
 * ARIA pattern and some interactions of a multiselect can't be translated to a
 * native <select> which is what `useSelect` tries to emulate.
 *
 * Some relevant discussions can be found here:
 * https://github.com/adobe/react-spectrum/issues/2140
 * https://github.com/adobe/react-spectrum/discussions/2218
 */

import { CollectionStateBase, Node, Selection } from '@react-types/shared';
import { ListState, useListState } from 'react-stately/useListState';
import { MultipleSelectionStateProps } from 'react-stately/useMultipleSelectionState';
import { useControlledState } from 'react-stately/useControlledState';
import patchSelectionManager, {
  SapphireSelectionManagerOptions,
} from '../../utils/patchSelectionManager';

export type MultiSelectListProps<T> = CollectionStateBase<T> &
  MultipleSelectionStateProps &
  Pick<SapphireSelectionManagerOptions, 'resolveSelectAllToKeys'>;

export type MultiSelectListState<T> = ListState<T> & {
  selectedItems: Node<T>[];
};

/**
 * Provides state management for list-like components with multiple selections.
 * Handles building a collection of items from props, and manages selection state.
 */
export function useMultiSelectListState<T extends object>(
  props: MultiSelectListProps<T>
): MultiSelectListState<T> {
  const [selectedKeys, setSelectedKeys] = useControlledState<Selection>(
    // @ts-expect-error minor typing issue in useControlledState
    props.selectedKeys,
    props.defaultSelectedKeys ?? new Set(),
    props.onSelectionChange
  );

  const { collection, disabledKeys, selectionManager } = useListState({
    ...props,
    filter: undefined, // we want to make sure we control this
    selectionMode: 'multiple',
    selectedKeys,
    onSelectionChange: setSelectedKeys,
  });

  patchSelectionManager(selectionManager, {
    resolveSelectAllToKeys: props.resolveSelectAllToKeys || false,
  });

  const selectedItems = Array.from(selectionManager.selectedKeys).flatMap(
    (key) => {
      const item = collection.getItem(key);
      return item === null ? [] : [item];
    }
  );

  return {
    collection,
    selectedItems,
    disabledKeys,
    selectionManager,
  };
}
